import {
  IconBuildingHospital,
  IconBuildingSkyscraper,
  IconCalendarEvent,
  IconFileStack,
  IconFlag,
  IconListCheck,
  IconMicroscope,
  IconReceipt2,
  IconReportMedical,
  IconSitemap,
  IconStethoscope,
  IconTextPlus,
  IconUsers,
} from "@tabler/icons-react";

import { DevelopmentFeatureFlags, PracticeFeatureFlags } from "@/feature-flags";
import { DOT_PHRASE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";

import { buildSettingsPath } from "../../utils/settings-paths";

import { SettingsNavLinkConfig } from "./types";

/** Every settings section, in sidebar order, before each one gets its absolute path. */
const SETTINGS_SECTIONS: Omit<SettingsNavLinkConfig, "buildPath">[] = [
  {
    id: "settings.profile",
    label: "Profile",
    keywords: ["practice", "address", "logo", "contact"],
    Icon: IconBuildingHospital,
    to: "profile",
    end: true,
    section: "Practice",
    gates: { disabledWhen: "non-manager" },
  },
  {
    id: "settings.users",
    label: "Users",
    keywords: ["staff", "team members", "roles", "permissions"],
    Icon: IconUsers,
    to: "users",
    end: false,
    section: "Practice",
    gates: { disabledWhen: "non-manager" },
  },
  {
    id: "settings.teams",
    label: "Teams",
    keywords: ["care teams", "groups", "members"],
    Icon: IconSitemap,
    to: "teams",
    end: false,
    section: "Practice",
  },
  {
    id: "settings.forms-library",
    label: "Forms Library",
    keywords: ["forms", "intake", "questionnaire", "templates"],
    Icon: IconFileStack,
    to: "forms/",
    end: false,
    section: "Practice",
  },
  {
    id: "settings.billing",
    label: "Billing",
    keywords: ["fees", "statements", "payments", "invoices"],
    Icon: IconReceipt2,
    to: "billing",
    end: false,
    section: "Practice",
    gates: {
      practiceFeatures: [PracticeFeatureFlags.Billing],
      disabledWhen: "non-biller",
    },
  },
  {
    id: "settings.lab-order-templates",
    label: "Lab Order Templates",
    keywords: ["labs", "tests", "orders", "panels"],
    Icon: IconMicroscope,
    to: "labs",
    end: false,
    section: "Templates",
    gates: { disabledWhen: "labs-inactive" },
  },
  {
    id: "settings.checklist-templates",
    label: "Checklist Templates",
    keywords: ["checklists", "tasks", "to-do"],
    Icon: IconListCheck,
    to: "checklist",
    end: false,
    section: "Templates",
  },
  {
    id: "settings.dotphrases",
    label: "Dotphrases",
    keywords: ["shortcuts", "snippets", "text", "notes"],
    Icon: IconTextPlus,
    to: "dot-phrases",
    end: false,
    section: "Templates",
    gates: {
      developmentFeatures: [DevelopmentFeatureFlags.DotPhrases],
      requiredPermissions: DOT_PHRASE_PERMISSION_FLAGS,
    },
  },
  {
    id: "settings.flag-library",
    label: "Flag Library",
    keywords: ["flags", "labels", "tags"],
    Icon: IconFlag,
    to: "flags",
    end: false,
    section: "Templates",
    gates: { developmentFeatures: [DevelopmentFeatureFlags.Flags] },
  },
  {
    id: "settings.locations",
    label: "Locations",
    keywords: ["offices", "clinics", "hours", "availability"],
    Icon: IconBuildingSkyscraper,
    to: "scheduling/location-hours",
    end: true,
    section: "Schedule",
  },
  {
    id: "settings.provider-hours",
    label: "Provider Hours",
    keywords: ["availability", "hours", "schedule", "providers"],
    Icon: IconStethoscope,
    to: "scheduling/provider-hours",
    end: true,
    section: "Schedule",
  },
  {
    id: "settings.appointment-types",
    label: "Appointment Types",
    keywords: ["visits", "services", "duration", "booking"],
    Icon: IconReportMedical,
    to: "scheduling/appointment-types",
    end: false,
    section: "Schedule",
  },
  {
    id: "settings.practice-schedule",
    label: "Practice Schedule",
    keywords: ["calendar", "schedules", "hours"],
    Icon: IconCalendarEvent,
    to: "scheduling/schedules",
    end: true,
    section: "Schedule",
  },

  //  TODO(PE-5402): Uncomment this when the Appointment Notification Note is implemented. The
  //  registry has no always-disabled gate, so add one to `DestinationGates` first.
  // {
  //   id: "settings.appointment-notification-note",
  //   label: "Appointment Notification Note",
  //   Icon: IconMessageChatbot,
  //   to: "..",
  //   end: true,
  // },
];

/** Every settings section, in sidebar order. `useSettingsNavLinks` resolves the gates. */
export const SETTINGS_NAV_LINKS: SettingsNavLinkConfig[] = SETTINGS_SECTIONS.map((section) => ({
  ...section,
  buildPath: ({ practiceId }) => buildSettingsPath(practiceId, section.to),
}));
